import * as Tone from 'tone';

export class TonalMatrix {
  constructor() {
    // Create base synth
    this.synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: {
        type: 'triangle',
        modulationType: 'sine'
      },
      envelope: {
        attack: 0.2,
        decay: 0.3,
        sustain: 0.8,
        release: 3
      }
    });

    // Add filter before other effects
    this.filter = new Tone.Filter({
      type: 'highpass',
      frequency: 100,
      rolloff: -12
    });

    // Create slower arpeggiator with callback to handle filter
    this.arpeggiator = new Tone.Pattern((time, note) => {
      // Get current position in the pattern
      const position = this.arpeggiator.index % 8; // 8 steps in pattern
      
      // Increase filter frequency as pattern progresses
      const baseFreq = 100;
      const maxFreq = 2000;
      const filterFreq = baseFreq + (position / 7) * (maxFreq - baseFreq);
      this.filter.frequency.setValueAtTime(filterFreq, time);
      
      this.synth.triggerAttackRelease(note, '8n', time);
    }, [], 'up');

    // Slow down the arpeggiator
    this.arpeggiator.interval = '8n';

    // Effects chain
    this.distortion = new Tone.Distortion({
      distortion: 0,
      wet: 0
    });

    this.reverb = new Tone.Reverb({
      decay: 8,
      wet: 0.7,
      preDelay: 0.2
    }).toDestination();

    this.delay = new Tone.FeedbackDelay({
      delayTime: 0.5,
      feedback: 0.3,
      wet: 0.4
    });

    // Chain everything together
    this.synth.chain(this.filter, this.distortion, this.delay, this.reverb, Tone.Destination);

    // Helper function to expand chord over two octaves
    this.expandChordTwoOctaves = (chord) => {
      const expanded = [...chord];
      // Add notes one octave up
      chord.forEach(note => {
        const noteBase = note.slice(0, -1);
        const octave = parseInt(note.slice(-1));
        expanded.push(noteBase + (octave + 1));
      });
      return expanded;
    };

    // Key definitions with expanded chords
    this.keys = [
      {
        root: 'F#',
        chords: {
          bottom: {
            tonic: this.expandChordTwoOctaves(['F#3', 'A3', 'C#4', 'F#4']),
            mediant: this.expandChordTwoOctaves(['A3', 'C#4', 'E4', 'A4']),
            submediant: this.expandChordTwoOctaves(['D3', 'F#3', 'A3', 'D4'])
          },
          top: {
            dominant: this.expandChordTwoOctaves(['C#3', 'E3', 'G#3', 'C#4']),
            dominantSeventh: this.expandChordTwoOctaves(['C#3', 'E3', 'G#3', 'B3']),
            tritoneSubstitution: this.expandChordTwoOctaves(['G3', 'B3', 'D4', 'F4'])
          },
          transition: {
            sus: this.expandChordTwoOctaves(['F#3', 'B3', 'C#4', 'F#4']),
            resolution: this.expandChordTwoOctaves(['F#3', 'A#3', 'C#4', 'F#4'])
          }
        }
      },
      {
        root: 'C#',
        chords: {
          bottom: {
            tonic: this.expandChordTwoOctaves(['C#3', 'E3', 'G#3', 'C#4']),
            mediant: this.expandChordTwoOctaves(['E3', 'G#3', 'B3', 'E4']),
            submediant: this.expandChordTwoOctaves(['A3', 'C#4', 'E4', 'A4'])
          },
          top: {
            dominant: this.expandChordTwoOctaves(['G#3', 'B3', 'D#4', 'G#4']),
            dominantSeventh: this.expandChordTwoOctaves(['G#3', 'B3', 'D#4', 'F#4']),
            tritoneSubstitution: this.expandChordTwoOctaves(['D3', 'F#3', 'A3', 'C4'])
          },
          transition: {
            sus: this.expandChordTwoOctaves(['C#3', 'F#3', 'G#3', 'C#4']),
            resolution: this.expandChordTwoOctaves(['C#3', 'F3', 'G#3', 'C#4'])
          }
        }
      },
      {
        root: 'B',
        chords: {
          bottom: {
            tonic: this.expandChordTwoOctaves(['B2', 'D3', 'F#3', 'B3']),
            mediant: this.expandChordTwoOctaves(['D3', 'F#3', 'A3', 'D4']),
            submediant: this.expandChordTwoOctaves(['G3', 'B3', 'D4', 'G4'])
          },
          top: {
            dominant: this.expandChordTwoOctaves(['F#3', 'A3', 'C#4', 'F#4']),
            dominantSeventh: this.expandChordTwoOctaves(['F#3', 'A3', 'C#4', 'E4']),
            tritoneSubstitution: this.expandChordTwoOctaves(['C3', 'E3', 'G3', 'Bb3'])
          },
          transition: {
            sus: this.expandChordTwoOctaves(['B2', 'E3', 'F#3', 'B3']),
            resolution: this.expandChordTwoOctaves(['B2', 'D#3', 'F#3', 'B3'])
          }
        }
      }
    ];

    this.currentKeyIndex = 0;
    this.lastKeyChangeTime = Date.now();
    this.keyChangeDuration = 15000; // 15 seconds
    this.activeNotes = new Set();
    this.lastTriggerTime = 0;
    this.isTransitioning = false;
  }

  start() {
    Tone.start();
    Tone.Transport.start();
    this.arpeggiator.start();
  }

  getCurrentKey() {
    const now = Date.now();
    if (now - this.lastKeyChangeTime > this.keyChangeDuration) {
      this.transitionToNewKey();
    }
    return this.keys[this.currentKeyIndex];
  }

  async transitionToNewKey() {
    this.isTransitioning = true;
    const currentKey = this.keys[this.currentKeyIndex];
    
    // Play transition chords
    await this.playChord(currentKey.chords.transition.sus, 2000);
    await this.playChord(currentKey.chords.transition.resolution, 2000);
    
    // Change key
    this.currentKeyIndex = (this.currentKeyIndex + 1) % this.keys.length;
    this.lastKeyChangeTime = Date.now();
    this.isTransitioning = false;
  }

  getChordFromPosition(x, y, width, height) {
    const currentKey = this.getCurrentKey();
    const isTopRow = y < height / 2;
    const horizontalSection = Math.floor((x / width) * 3);
    
    if (isTopRow) {
      switch(horizontalSection) {
        case 0: return currentKey.chords.top.dominant;
        case 1: return currentKey.chords.top.dominantSeventh;
        case 2: return currentKey.chords.top.tritoneSubstitution;
        default: return currentKey.chords.top.dominant;
      }
    } else {
      switch(horizontalSection) {
        case 0: return currentKey.chords.bottom.tonic;
        case 1: return currentKey.chords.bottom.mediant;
        case 2: return currentKey.chords.bottom.submediant;
        default: return currentKey.chords.bottom.tonic;
      }
    }
  }

  async playChord(chord, duration) {
    this.arpeggiator.values = chord;
    
    return new Promise(resolve => {
      setTimeout(resolve, duration);
    });
  }

  triggerNote(x, y, width, height) {
    if (this.isTransitioning) return;
    
    const now = Tone.now();
    if (now - this.lastTriggerTime < 1) return;

    const chord = this.getChordFromPosition(x, y, width, height);
    const chordKey = chord.join(',');

    if (!this.activeNotes.has(chordKey)) {
      this.activeNotes.add(chordKey);
      
      // Scale distortion based on horizontal position
      const distortionAmount = (x / width) * 0.3;
      this.distortion.distortion = distortionAmount;
      this.distortion.wet.value = distortionAmount;

      // Reset filter frequency for new pattern
      this.filter.frequency.value = 100;

      // Update arpeggiator with expanded chord
      this.arpeggiator.values = chord;
      
      setTimeout(() => {
        this.activeNotes.delete(chordKey);
      }, 4000);

      this.lastTriggerTime = now;
    }
  }

  cleanup() {
    this.arpeggiator.stop();
    this.arpeggiator.dispose();
    this.synth.dispose();
    this.filter.dispose();
    this.distortion.dispose();
    this.reverb.dispose();
    this.delay.dispose();
  }
}